import { ChatUser, ConversationDetails } from "@virtualprogaming/js-public-sdk";
import classNames from "classnames";
import { isEmpty, map, partial } from "lodash";
import React, { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { useChatCreateSystemConversation } from "~/queries";
import { Button } from "~/ui/components/buttons";
import { Icon, LoadingIcon } from "~/ui/components/icons";
import { TextInput } from "~/ui/components/inputs";
import { SelectedWindows, useChatContext } from "~/ui/context";
import { useScreenMd } from "~/utils";

import { CreateChat } from "../createChat/CreateChat";

interface ChatDetailsForm {
  name: string;
  slug: string;
  description: string;
}

export enum SystemChatSteps {
  Details = "Details",
  Users = "Users",
}

export const CreateSystemChat: FC = () => {
  const isDesktop = useScreenMd();
  const intl = useIntl();
  const { setSelectedChat, setSelectedWindow } = useChatContext();
  const [selectedUsers, setSelectedUsers] = useState<ChatUser[]>([]);

  const [step, setStep] = useState<SystemChatSteps>(SystemChatSteps.Details);

  const {
    mutateAsync: createConversation,
    isLoading: isCreateConversationLoading,
  } = useChatCreateSystemConversation();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<ChatDetailsForm>();

  const handleCreateConversation = (params: ChatDetailsForm) => {
    if (isEmpty(selectedUsers)) return;
    const userIds = map(selectedUsers, ({ id }) => id ?? 0);
    createConversation([
      params.name,
      params.slug,
      params.description,
      undefined,
      { user_ids: userIds, tags: ["global"] },
    ]).then((res: { status: number; data: ConversationDetails }) => {
      if (res.status === 200 && res?.data?.id) {
        setSelectedChat(res.data.id);
      }
      setSelectedWindow(SelectedWindows.Conversation);
    });
  };

  return (
    <div className="h-95 flex flex-col justify-between relative">
      <form
        onSubmit={handleSubmit(handleCreateConversation)}
        className="h-full"
      >
        <div
          className={classNames(
            "flex flex-col gap-md mg-0-auto h-95 pd-t-md",
            isDesktop ? "w-80" : "w-90"
          )}
        >
          {step === SystemChatSteps.Details ? (
            <div className="flex flex-col gap-md">
              <TextInput
                label={intl.formatMessage({
                  defaultMessage: "Chat name",
                  id: "oaHo7P",
                })}
                error={errors.name}
                min={0}
                max={20}
                step={1}
                {...register("name", { required: true })}
              />
              <TextInput
                label={intl.formatMessage({
                  defaultMessage: "Slug",
                  id: "IoDlcd",
                })}
                error={errors.slug}
                min={0}
                max={20}
                step={1}
                {...register("slug", { required: true })}
              />
              <TextInput
                label={intl.formatMessage({
                  defaultMessage: "Description",
                  id: "Q8Qw5B",
                })}
                error={errors.description}
                min={0}
                max={20}
                step={1}
                {...register("description", { required: true })}
              />
            </div>
          ) : (
            <div className="h-85">
              <CreateChat
                outsideSelectedUsers={selectedUsers}
                setOutsideSelectedUsers={setSelectedUsers}
                shouldSubmit={false}
              />
            </div>
          )}
          <>
            {step === SystemChatSteps.Details && (
              <div
                className={classNames(
                  "flex w-100",
                  step === SystemChatSteps.Details
                    ? "justify-end"
                    : "justify-between"
                )}
              >
                <Button
                  variant="success"
                  size="md"
                  className="flex gap-sm items-center font-variant-small-caps"
                  onClick={partial(setStep, SystemChatSteps.Users)}
                >
                  <FormattedMessage defaultMessage="Select users" id="WSKUqU" />

                  <div>
                    <Icon icon={["far", "angle-right"]} />
                    <Icon icon={["fas", "users"]} size="lg" />
                  </div>
                </Button>
              </div>
            )}
            <div className="flex w-100 justify-between">
              {step === SystemChatSteps.Users && (
                <Button
                  variant="secondary"
                  size="md"
                  className="flex gap-sm items-center font-variant-small-caps"
                  onClick={partial(setStep, SystemChatSteps.Details)}
                >
                  <div>
                    <Icon icon={["fal", "pen"]} size="lg" />
                  </div>
                  <FormattedMessage defaultMessage="Edit details" id="BVWzJ5" />
                </Button>
              )}
              {step === SystemChatSteps.Users && (
                <Button
                  variant="success"
                  size="md"
                  className="flex gap-sm items-center font-variant-small-caps"
                >
                  <FormattedMessage defaultMessage="Create chat" id="8cTD8S" />
                  {isCreateConversationLoading ? (
                    <LoadingIcon size="lg" />
                  ) : (
                    <Icon icon={["far", "paper-plane"]} size="lg" />
                  )}
                </Button>
              )}
            </div>
          </>
        </div>
      </form>
    </div>
  );
};
